/**
 * Reads UserConfig (once authenticated) and applies theme; reads
 * DeviceConfig and applies the per-device sizing CSS variables. Falls
 * back to DEVICE_CONFIG_DEFAULTS for any unset device-config field.
 * Also listens to prefers-color-scheme for AUTO mode.
 */
import { useEffect } from 'react';
import {
  useUserConfig,
  useDeviceConfig,
  DEVICE_CONFIG_DEFAULTS,
} from '../api/config';
import { useSession } from '../api/session';

export function ThemeMount(): null {
  const { data: session } = useSession();
  const { data: cfg } = useUserConfig();
  const { data: dc } = useDeviceConfig();

  useEffect(() => {
    const root = document.documentElement;
    if (!session?.user || !cfg) return;
    root.setAttribute('data-theme', cfg.theme);
    try {
      localStorage.setItem('sl.theme', cfg.theme);
    } catch { /* noop */ }
    if (cfg.theme === 'AUTO') {
      const m = window.matchMedia('(prefers-color-scheme: light)');
      const apply = (): void => root.setAttribute('data-theme-prefers', m.matches ? 'light' : 'dark');
      apply();
      m.addEventListener('change', apply);
      return (): void => m.removeEventListener('change', apply);
    }
    return undefined;
  }, [session, cfg]);

  useEffect(() => {
    const root = document.documentElement;
    const iconSize = dc?.iconSize ?? DEVICE_CONFIG_DEFAULTS.iconSize;
    const pageScale = dc?.pageScale ?? DEVICE_CONFIG_DEFAULTS.pageScale;
    const itemSpacing = dc?.itemSpacing ?? DEVICE_CONFIG_DEFAULTS.itemSpacing;
    root.style.setProperty('--sl-icon-size', `${iconSize}px`);
    root.style.setProperty('--sl-page-scale', String(pageScale));
    root.style.setProperty('--sl-item-spacing', `${itemSpacing}px`);
  }, [dc]);

  return null;
}
